import { gql, useQuery } from "@apollo/client";
import { Card, Typography } from "@material-ui/core";
import { Button } from "@resource/atlas/button";
import { atlasClose } from "@resource/atlas/icons";
import cx from "clsx";
import {
  GetFeedbackData,
  GetFeedbackDataVariables,
} from "generated/schemaTypes";
import { join } from "lodash";
import React, { useEffect, useState } from "react";
import { analytics } from "react-hooks/useAnalytics";

import DescriptionDivider from "./DescriptionDivider";
import {
  EmojiStep,
  FeedbackStepEnum,
  NPSStep,
  ThankYouStep,
  WhatCouldBeBetterStep,
  WhatWentWellStep,
} from "./steps";
import { Option } from "./steps/EmojiStep/options";

type ControllerProps = {
  guideId: string;
  dismissFeedback(): void;
  allowClose?: boolean;
  isDemo?: boolean;
  isSample?: boolean;
  enableNPS: boolean;
  needsFeedback: boolean;
  showTrustSurvey: boolean;
  isGuideCandidate: boolean;
  onSubmit(data: GraphQL_JSON): Promise<void>;
};

const GET_FEEDBACK_DATA = gql`
  query GetFeedbackData($where: GuideWhereUniqueInput!) {
    guideFindUnique(where: $where) {
      id
      candidate {
        id
        organization {
          id
          name
        }
      }
    }
  }
`;

function Controller({
  dismissFeedback,
  isDemo = false,
  isSample = false,
  allowClose = false,
  guideId,
  enableNPS,
  onSubmit,
  needsFeedback,
  showTrustSurvey,
  isGuideCandidate,
}: ControllerProps) {
  let initialStep: FeedbackStepEnum | null = null;
  if (needsFeedback) {
    initialStep = FeedbackStepEnum.EmojiStep;
  }
  useEffect(() => {
    if (initialStep !== null) {
      analytics.track("Controller Module Loaded");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState<FeedbackStepEnum | null>(
    initialStep
  );
  const [selectedEmoji, setSelectedEmoji] = useState<string | undefined>();
  const [npsScore, setNpsScore] = useState<number>();
  const [descriptionText, setDescriptionText] = useState<string | null>(null);
  const [whatWentWellCategories, setWhatWentWellCatgories] = useState<string[]>(
    []
  );

  const [whatWentWellFreeform, setWhatWentWellFreeform] = useState("");

  const { data: feedbackData } = useQuery<
    GetFeedbackData,
    GetFeedbackDataVariables
  >(GET_FEEDBACK_DATA, {
    variables: {
      where: {
        id: guideId,
      },
    },
    skip: isDemo,
  });

  const companyName = isDemo
    ? "Good Health"
    : feedbackData?.guideFindUnique.candidate.organization.name ?? "";

  const finish = async (
    whatCouldBeBetterFreeform: string,
    whatCouldBeBetterCategories: string[]
  ): Promise<void> => {
    try {
      if (!isDemo) {
        setSaving(true);
        await onSubmit({
          data: {
            emoji: selectedEmoji,
            whatWentWellFreeform,
            whatCouldBeBetterFreeform,
            whatWentWellCategories,
            whatCouldBeBetterCategories,
            npsScore,
          },
        });
        analytics.track("Feedback Submitted", {
          customerSuccess: true,
        });
      }
      setCurrentStep(FeedbackStepEnum.ThankYouStep);
    } catch (err) {
      console.error("err: ", err);
    } finally {
      setSaving(false);
    }
  };

  if (currentStep === null) {
    return null;
  }

  return (
    <Card
      className={cx("p-8 max-w-lg", {
        "mt-5": isSample,
      })}
    >
      {(allowClose || currentStep === FeedbackStepEnum.ThankYouStep) && (
        <div className="absolute top-2 right-2">
          <Button
            isGhost
            icon={atlasClose}
            onClick={
              isSample && currentStep === FeedbackStepEnum.ThankYouStep
                ? (): void => {
                    setCurrentStep(FeedbackStepEnum.EmojiStep);
                  }
                : dismissFeedback
            }
          />
        </div>
      )}
      {((): React.ReactNode | null => {
        switch (currentStep) {
          case FeedbackStepEnum.EmojiStep:
            return (
              <EmojiStep
                setDescriptionText={setDescriptionText}
                onOptionSelected={(option: Option): void => {
                  setSelectedEmoji(option.emoji);
                  setDescriptionText(null);
                  if (enableNPS) {
                    analytics.track(
                      "Feedback Emoji Rating Selected (NPS Enabled)",
                      {
                        value: option.value,
                      }
                    );
                    setCurrentStep(FeedbackStepEnum.NPSStep);
                  } else {
                    analytics.track("Feedback Emoji Rating Selected", {
                      value: option.value,
                    });
                    setCurrentStep(FeedbackStepEnum.WhatWentWellStep);
                  }
                }}
              />
            );
          case FeedbackStepEnum.NPSStep:
            return (
              <NPSStep
                setDescriptionText={setDescriptionText}
                companyName={companyName}
                onSubmitNPS={(score: number): void => {
                  analytics.track("Feedback NPS Score Submitted", {
                    score,
                  });
                  setNpsScore(score);
                  setDescriptionText(null);
                  setCurrentStep(FeedbackStepEnum.WhatWentWellStep);
                }}
              />
            );
          case FeedbackStepEnum.WhatWentWellStep:
            return (
              <WhatWentWellStep
                onSubmitDetailedFeedback={(freeform, categories): void => {
                  analytics.track("Feedback What Went Well Submitted", {
                    categories: join(categories, ", "),
                    freeform,
                  });
                  setWhatWentWellCatgories(categories);
                  setWhatWentWellFreeform(freeform);
                  setCurrentStep(FeedbackStepEnum.WhatCouldBeBetterStep);
                }}
              />
            );
          case FeedbackStepEnum.WhatCouldBeBetterStep:
            return (
              <WhatCouldBeBetterStep
                submitting={saving}
                onSubmitDetailedFeedback={(freeform, categories): void => {
                  analytics.track("Feedback What Could Be Better Submitted", {
                    categories: join(categories, ", "),
                    freeform,
                  });
                  finish(freeform, categories);
                }}
                buttonText={showTrustSurvey ? "Next" : "Submit"}
              />
            );
          case FeedbackStepEnum.ThankYouStep:
            return <ThankYouStep isGuideCandidate={isGuideCandidate} />;
          default:
            return null;
        }
      })()}
      {currentStep !== FeedbackStepEnum.ThankYouStep && (
        <>
          <DescriptionDivider text={descriptionText} />
          <Typography
            variant="subtitle2"
            className="mt-2 text-small text-subtle"
          >
            Your feedback is anonymous and safe with us. The feedback you share
            is aggregated to protect your identity and will not be shared until
            you complete your hiring journey.
          </Typography>
        </>
      )}
    </Card>
  );
}

export default Controller;
